import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getMonthLocks,
  getProjectMonthLocks,
  lockProjectMonth,
  unlockProjectMonth,
  monthLockKey,
  type LockProjectMonthInput,
} from '../services/api/projectMonthLocks';
import { invalidateMany } from './queryUtils';

// Phase 113 (MKS-208) — project-month entry locks.

/**
 * Caches a lock/unlock has to refresh: both lock reads, plus the time-entry
 * grid, whose cells become (un)writable the moment the lock flips.
 */
function invalidateMonthLockCaches(qc: QueryClient, projectId: number): void {
  invalidateMany(qc, [
    ['projectMonthLocks', projectId],
    ['monthLocks'],
    ['timeEntries'],
  ]);
}

/** Every locked project for one calendar month, for the time-entry grid. */
export function useMonthLocks(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: ['monthLocks', year, month],
    queryFn: () => getMonthLocks(year, month),
    enabled,
  });
}

/**
 * The same data as a `Set` of `${projectId}-${YYYY}-${MM}` keys, which is what
 * the grid looks cells up by. Separate from the timesheet-submission lock the
 * grid already has: that one is date-only (a submitted timesheet closes the
 * whole month for one person across every project), while this one is per
 * (project, month) — project A can be closed for August while project B is
 * open for the same person in the same month.
 */
export function useMonthLockKeys(year: number, month: number, enabled = true): Set<string> {
  const { data } = useMonthLocks(year, month, enabled);
  return useMemo(
    () => new Set((data ?? []).map((l) => monthLockKey(l.projectId, l.year, l.month))),
    [data],
  );
}

/** Every locked month on one project, for the padlock panel. */
export function useProjectMonthLocks(projectId: number, enabled = true) {
  return useQuery({
    queryKey: ['projectMonthLocks', projectId],
    queryFn: () => getProjectMonthLocks(projectId),
    enabled,
  });
}

export function useLockProjectMonth(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LockProjectMonthInput) => lockProjectMonth(projectId, input),
    onSuccess: () => invalidateMonthLockCaches(queryClient, projectId),
  });
}

export function useUnlockProjectMonth(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      unlockProjectMonth(projectId, year, month),
    onSuccess: () => {
      // D-12: pending backdated-hours requests are deliberately untouched by an
      // unlock, so their caches are not invalidated here.
      invalidateMonthLockCaches(queryClient, projectId);
    },
  });
}
